/**
 * NL-019 — Weekly Focus Virtue prompt on /check-in.
 *
 * The Daily Check-in only PROMPTS for the focus virtue when the current ISO
 * week has none set yet (the weekly reset). Once a virtue is set, this card
 * disappears from the Check-in — the focus then lives on the Dashboard for
 * the rest of the week, where the client can change it. Step-gated to 5+.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import type { AffirmationGroupEntry, WeeklyFocusEntry } from '@/lib/api-client';
import { WeeklyFocusPicker } from '@/components/weekly-focus-picker';

interface WeeklyFocusCardProps {
  readonly clientId: string;
  readonly currentStep: number;
}

/** Monday of the ISO week containing `now` — YYYY-MM-DD in UTC. */
function isoMondayUtc(now: Date = new Date()): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const day = d.getUTCDay() === 0 ? 7 : d.getUTCDay();
  d.setUTCDate(d.getUTCDate() - (day - 1));
  return d.toISOString().slice(0, 10);
}

export function WeeklyFocusCard({
  clientId,
  currentStep,
}: WeeklyFocusCardProps): JSX.Element | null {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const weekStart = useMemo(() => isoMondayUtc(), []);

  const [current, setCurrent] = useState<WeeklyFocusEntry | null>(null);
  const [virtues, setVirtues] = useState<AffirmationGroupEntry[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (currentStep < 5 || !clientId) {
      setHydrated(true);
      return;
    }
    let active = true;
    void (async () => {
      try {
        const [focus, affirmations] = await Promise.all([
          api.getWeeklyFocus(accessToken, clientId),
          api.getAffirmationsByGroup(accessToken, clientId),
        ]);
        if (!active) return;
        // Carpe-Diem-only virtues (the spec names Carpe Diem virtues).
        setVirtues(affirmations.inMatrix);
        setCurrent(focus.current);
      } catch {
        if (active) setError(t('weeklyFocus.errors.load'));
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId, currentStep, t]);

  if (currentStep < 5) return null;
  // No flash while loading; and once this week's virtue is set the prompt
  // leaves the Check-in (it now lives on the Dashboard).
  if (!hydrated) return null;
  if (current?.virtueId) return null;

  // Unset → prompt. If the matrix has no virtues yet, explain instead.
  if (virtues.length === 0) {
    return (
      <section
        className="mb-s5 rounded-lg border border-bone-3 bg-bone-1 p-s5"
        data-testid="weekly-focus-card-needs-virtues"
      >
        <header className="mb-s2">
          <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
            {t('weeklyFocus.eyebrow')}
          </p>
          <h2 className="font-display text-display-sm text-ink mt-s1">
            {t('weeklyFocus.title')}
          </h2>
        </header>
        <p className="text-sm text-gray-2">{t('weeklyFocus.needsVirtues')}</p>
      </section>
    );
  }

  return (
    <section
      className="mb-s5 rounded-lg border border-bone-3 bg-bone-1 p-s5"
      data-testid="weekly-focus-card-picker"
    >
      <header className="mb-s2">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('weeklyFocus.eyebrow')}
        </p>
        <h2 className="font-display text-display-sm text-ink mt-s1">
          {t('weeklyFocus.title')}
        </h2>
      </header>
      {error && (
        <p className="mb-s2 text-sm text-flame-deep" role="alert">
          {error}
        </p>
      )}
      <WeeklyFocusPicker
        clientId={clientId}
        weekStart={weekStart}
        virtues={virtues}
        current={current}
        onSaved={(saved) => setCurrent(saved)}
      />
    </section>
  );
}
